import { useState } from 'react';

import { Button } from '../../button';

import {
  AGREEMENT_BADGE,
  agreementLine,
  agreementSummary,
  agreementTable,
  agreementTransition,
  canJudgeNext,
  COST_OPTIONS,
  costOptionLabels,
  costRows,
  costSummary,
  DRIFT_NOTE_IDS,
  driftPairs,
  driftRun,
  driftSummary,
  driftTransition,
  formatCount,
  GRADE_NOTE_IDS,
  gradePairLabel,
  gradePairs,
  gradeSummary,
  gradeTransition,
  gradeUserMessage,
  HIGHLIGHTS,
  highlightedPairs,
  highlightLabels,
  initialAgreement,
  initialCost,
  initialDrift,
  initialGrade,
  JUDGE_SYSTEM_MESSAGE,
  judgedPairs,
  NOT_ASKED,
  PAIR_COUNT,
  titleLengthLabel,
  toCostOption,
  toDriftNoteId,
  toGradeNoteId,
  toHighlight,
  toVersion,
  versionLabel,
  VERSIONS,
  type AgreementAction,
  type DriftAction,
  type GradeAction,
} from './judge-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';

import './relationship-labs.css';
import './judge-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
  className = '',
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
  className?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className={`rel-table cb-record-grid ${className}`} role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column, index) => (
            <span key={index} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const bullets = (titles: readonly string[]) =>
  titles.length ? titles.map((title) => `- ${title}`).join('\n') : '(none)';
const VERDICT_COLUMNS = ['Grader', 'Verdict', 'Says'];
const VERDICT_TEMPLATE = 'minmax(0, 0.8fr) minmax(0, 0.7fr) minmax(0, 3fr)';
const REPLAY = 'This graphic replays real runs.';

export function JudgeGradeLab() {
  const [state, setState] = useState(initialGrade);
  const act = (action: GradeAction) => setState((current) => gradeTransition(current, action));
  const pair = gradePairs[state.pair];
  const summary = gradeSummary(state);
  return (
    <Lab
      title="What does the judge see, and what does it answer?"
      eyebrow="Judge model · one pair"
      className="rel-lab judge-lab"
    >
      <Predict>
        Will the judge pass this pair? Read the note and both lists, then press Ask the judge.
      </Predict>
      <div className="cb-controls">
        <label>
          Pair
          <ModelSelect
            aria-label="Pair"
            value={String(state.pair)}
            onValueChange={(value) => act({ type: 'pair', value: toGradeNoteId(value) })}
            options={GRADE_NOTE_IDS.map((id) => ({ value: String(id), label: gradePairLabel(id) }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.showPrompt}
          onClick={() => act({ type: 'toggle-prompt' })}
        >
          Show the judge's prompt
        </Button>
        <Button size="sm" disabled={state.asked} onClick={() => act({ type: 'ask' })}>
          Ask the judge
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      {state.showPrompt ? (
        <div className="cb-two">
          <Panel label="System message">
            <pre aria-label="System message">{JUDGE_SYSTEM_MESSAGE}</pre>
          </Panel>
          <Panel label="User message">
            <pre aria-label="User message">{gradeUserMessage(pair)}</pre>
          </Panel>
        </div>
      ) : null}
      <Panel label="Note">
        <pre aria-label="Note">{pair.note}</pre>
      </Panel>
      <div className="cb-two">
        <Panel label="Expected tasks">
          <pre aria-label="Expected tasks">{bullets(pair.expected)}</pre>
        </Panel>
        <Panel label="Suggested tasks">
          <pre aria-label="Suggested tasks">{bullets(pair.suggested)}</pre>
        </Panel>
      </div>
      <Table
        label="Verdicts"
        className="judge-grid"
        columns={VERDICT_COLUMNS}
        template={VERDICT_TEMPLATE}
        rows={[
          ['Keyword grader', pair.keywords.verdict, pair.keywords.reasons.join('; ')],
          state.asked
            ? ['Judge', pair.judge.verdict, pair.judge.critique]
            : ['Judge', NOT_ASKED, ''],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function JudgeAgreementLab() {
  const [state, setState] = useState(initialAgreement);
  const act = (action: AgreementAction) =>
    setState((current) => agreementTransition(current, action));
  const pairs = judgedPairs(state);
  const table = agreementTable(pairs);
  const summary = agreementSummary(state);
  return (
    <Lab
      title="How often does the judge agree with a person, and where?"
      eyebrow="Judge model · the check"
      className="rel-lab judge-lab"
    >
      <Predict>
        How many of 20 pairs will the judge and the person agree on? Press Judge the next pair until
        all are judged.
      </Predict>
      <div className="cb-controls">
        <Button size="sm" disabled={!canJudgeNext(state)} onClick={() => act({ type: 'next' })}>
          Judge the next pair
        </Button>
        <label>
          Highlight
          <ModelSelect
            aria-label="Highlight"
            value={state.highlight}
            onValueChange={(value) => act({ type: 'highlight', value: toHighlight(value) })}
            options={HIGHLIGHTS.map((highlight) => ({
              value: highlight,
              label: highlightLabels[highlight],
            }))}
          />
        </label>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="Agreement"
        className="judge-grid"
        columns={['', 'Judge pass', 'Judge fail']}
        rows={[
          ['Person pass', table.passPass, table.passFail],
          ['Person fail', table.failPass, table.failFail],
        ]}
      />
      <Facts
        values={
          state.judged
            ? [
                ['Judged', `${state.judged} of ${PAIR_COUNT}`],
                ['Agreement', agreementLine(state)],
              ]
            : [['Judged', `${state.judged} of ${PAIR_COUNT}`]]
        }
      />
      <Table
        label="Judged pairs"
        columns={['Pair', 'Person', 'Judge', 'Critique']}
        template="minmax(0, 0.5fr) minmax(0, 0.7fr) minmax(0, 0.7fr) minmax(0, 3fr)"
        rows={highlightedPairs(state).map((pair) => [
          pair.id,
          pair.person,
          pair.judge,
          pair.critique,
        ])}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function JudgeDriftLab() {
  const [state, setState] = useState(initialDrift);
  const act = (action: DriftAction) => setState((current) => driftTransition(current, action));
  const pair = driftPairs[state.pair];
  const run = driftRun(state);
  const summary = driftSummary(state);
  return (
    <Lab
      title="Does a judge checked on version 1 stay right on version 2?"
      eyebrow="Judge model · drift"
      className="rel-lab judge-lab"
    >
      <Predict>
        Will the judge stay right on version 2? Set Feature prompt version to Version 2, then press
        Ask the judge.
      </Predict>
      <p className="judge-badge">{AGREEMENT_BADGE}</p>
      <div className="cb-controls">
        <label>
          Pair
          <ModelSelect
            aria-label="Pair"
            value={String(state.pair)}
            onValueChange={(value) => act({ type: 'pair', value: toDriftNoteId(value) })}
            options={DRIFT_NOTE_IDS.map((id) => ({ value: String(id), label: `Note ${id}` }))}
          />
        </label>
        <label>
          Feature prompt version
          <ModelSelect
            aria-label="Feature prompt version"
            value={String(state.version)}
            onValueChange={(value) => act({ type: 'version', value: toVersion(value) })}
            options={VERSIONS.map((version) => ({
              value: String(version),
              label: versionLabel(version),
            }))}
          />
        </label>
        <Button size="sm" disabled={state.asked} onClick={() => act({ type: 'ask' })}>
          Ask the judge
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <div className="cb-two">
        <Panel label="Note">
          <pre aria-label="Note">{pair.note}</pre>
        </Panel>
        <Panel label="Expected tasks">
          <pre aria-label="Expected tasks">{bullets(pair.expected)}</pre>
        </Panel>
      </div>
      <Table
        label="Suggested tasks"
        columns={['Suggested title', 'Characters']}
        template="minmax(0, 4fr) minmax(0, 1fr)"
        rows={run.suggested.map((title) => [title, titleLengthLabel(title)])}
      />
      <Table
        label="Verdicts"
        className="judge-grid"
        columns={VERDICT_COLUMNS}
        template={VERDICT_TEMPLATE}
        rows={[
          ['Code grader', run.code.verdict, run.code.reasons.join('; ')],
          state.asked ? ['Judge', run.judge.verdict, run.judge.critique] : ['Judge', NOT_ASKED, ''],
          state.asked
            ? ['Person', run.person.verdict, run.person.reasons.join('; ')]
            : ['Person', NOT_ASKED, ''],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function JudgeCostLab() {
  const [state, setState] = useState(initialCost);
  const rows = costRows(state);
  const summary = costSummary(state);
  return (
    <Lab
      title="What does judging cost compared with answering?"
      eyebrow="Judge model · cost"
      className="rel-lab judge-lab"
    >
      <Predict>
        Does judging the set cost more than running it? Press Include the suggester's own cost, then
        change What to judge.
      </Predict>
      <div className="cb-controls">
        <label>
          What to judge
          <ModelSelect
            aria-label="What to judge"
            value={state.option}
            onValueChange={(value) =>
              setState((current) => ({ ...current, option: toCostOption(value) }))
            }
            options={COST_OPTIONS.map((option) => ({
              value: option,
              label: costOptionLabels[option],
            }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.includeSuggester}
          onClick={() =>
            setState((current) => ({ ...current, includeSuggester: !current.includeSuggester }))
          }
        >
          Include the suggester's own cost
        </Button>
        <Reset onClick={() => setState(initialCost())} />
      </div>
      <Table
        label="Cost"
        className="judge-grid judge-wide"
        columns={[
          'Who',
          'Calls',
          'Prompt tokens',
          'Completion tokens',
          'Cost (example rate)',
          'Time added per request (p50 / p95)',
        ]}
        template="minmax(0, 0.9fr) minmax(0, 0.6fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.6fr)"
        rows={rows.map((row) => [
          row.who,
          formatCount(row.calls),
          formatCount(row.promptTokens),
          formatCount(row.completionTokens),
          row.cost,
          row.time,
        ])}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
